import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Routes, Route } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { LanguageProvider } from './lib/LanguageContext.tsx';
import './App.css';
import App from './App.tsx';
import { Login } from './pages/Login.tsx';
import { ResetPassword } from './pages/ResetPassword.tsx';
import { ProtectedRoute } from './components/ProtectedRoute.tsx';

// Page imports
import { Dashboard } from './pages/Dashboard.tsx';
import { Programs } from './pages/Programs.tsx';
import { CreateProgram } from './pages/CreateProgram.tsx';
import { ProgramDetail } from './pages/ProgramDetail.tsx';
import { MaterialsWorkspace } from './pages/MaterialsWorkspace.tsx';
import { Registrations } from './pages/Registrations.tsx';
import { RegistrationDetail } from './pages/RegistrationDetail.tsx';
import { Participants } from './pages/Participants.tsx';
import { ParticipantDetail } from './pages/ParticipantDetail.tsx';
import { Settings } from './pages/Settings.tsx';
import { ReviewQueue } from './pages/ReviewQueue.tsx';
import { WhatsAppGroups } from './pages/WhatsAppGroups.tsx';
import { WhatsAppGroupDetail } from './pages/WhatsAppGroupDetail.tsx';
import { Evaluations } from './pages/Evaluations.tsx';
import { CreateEvaluation } from './pages/CreateEvaluation.tsx';
import { EvaluationDetail } from './pages/EvaluationDetail.tsx';
import { QuestionBank } from './pages/QuestionBank.tsx';
import { Reporting } from './pages/Reporting.tsx';
import { Users } from './pages/Users.tsx';
import { Profile } from './pages/Profile.tsx';
import { Announcements } from './pages/Announcements.tsx';
import { ParticipantCRM } from './pages/ParticipantCRM.tsx';
import { EmailCampaigns } from './pages/EmailCampaigns.tsx';

import { ErrorBoundary } from './components/ErrorBoundary.tsx';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <LanguageProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route
                element={
                  <ProtectedRoute>
                    <App />
                  </ProtectedRoute>
                }
              >
                <Route path="/" element={<Dashboard />} />
                <Route path="/programs" element={<Programs />} />
                <Route path="/programs/create" element={<CreateProgram />} />
                <Route path="/programs/:id" element={<ProgramDetail />} />
                <Route path="/materials" element={<MaterialsWorkspace />} />
                <Route path="/materials/review" element={<ReviewQueue />} />
                <Route path="/registrations" element={<Registrations />} />
                <Route path="/registrations/:id" element={<RegistrationDetail />} />
                <Route path="/participants" element={<Participants />} />
                <Route path="/participants/:id" element={<ParticipantDetail />} />
                <Route path="/crm" element={<ParticipantCRM />} />
                <Route path="/email-campaigns" element={<EmailCampaigns />} />
                <Route path="/email-campaigns/:id" element={<EmailCampaigns />} />
                <Route path="/users" element={<Users />} />
                <Route path="/evaluations" element={<Evaluations />} />
                <Route path="/evaluations/create" element={<CreateEvaluation />} />
                <Route path="/evaluations/:id" element={<EvaluationDetail />} />
                <Route path="/question-bank" element={<QuestionBank />} />
                <Route path="/announcements" element={<Announcements />} />
                <Route path="/whatsapp-groups" element={<WhatsAppGroups />} />
                <Route path="/whatsapp-groups/:id" element={<WhatsAppGroupDetail />} />
                <Route path="/reports" element={<Reporting />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/profile" element={<Profile />} />
              </Route>
            </Routes>
          </BrowserRouter>
        </LanguageProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>
);
